/*
 * Cart line-item reordering — CSS `order` based.
 *
 * Why CSS `order` and not DOM moves:
 *   The cart's qty inputs are `<input name="updates[]">` (no explicit index),
 *   which Shopify's /cart endpoint parses POSITIONALLY at form submission.
 *   If we used `insertBefore` to reorder rows visually, the form would submit
 *   the quantity array in the wrong order at checkout — silently swapping
 *   quantities between line items in the customer's actual cart.
 *
 *   By only changing CSS `order`, the DOM order stays = natural cart order =
 *   what Shopify expects. The checkout form, cart-items.js's per-line
 *   /cart/change.js calls (which use explicit `data-index`), and any other
 *   positional consumer all keep working unchanged.
 *
 * Persistence:
 *   - localStorage[bcs-cart-item-order]   authoritative client store
 *   - cart.attributes.line_item_order      server-side backup mirror,
 *                                          set via debounced POST /cart/update.js
 *
 * Rendering:
 *   - On every cart render (initial load, drawer open, cart re-render after
 *     qty/remove), order is reapplied before the user sees the cart.
 *   - Stale keys (items no longer in cart) pruned during sync passes.
 *   - New items (no stored position) appended at the visual end without
 *     disturbing existing positions.
 */

(function () {
  'use strict';

  if (window.__bcsCartReorderLoaded) return;
  window.__bcsCartReorderLoaded = true;

  var STORAGE_KEY = 'bcs-cart-item-order';
  var ATTR_KEY = 'line_item_order';
  var PENDING_ATTR = 'data-cart-reorder-pending';

  var ROW_SEL = '.js-cart-reorder-row';
  var UP_SEL = '.js-cart-reorder-up';
  var DOWN_SEL = '.js-cart-reorder-down';
  var BTN_SEL = UP_SEL + ', ' + DOWN_SEL;
  var ANNOUNCE_SEL = '.js-cart-reorder-announce';
  var FLASH_CLASS = 'cart-item--just-moved';
  var HIDDEN_CLASS = 'cart-item__reorder-btn--hidden';

  // ----- localStorage -----

  function loadOrder() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (k) { return typeof k === 'string' && k.length > 0; });
    } catch (e) {
      return [];
    }
  }

  function saveOrder(order) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
    } catch (e) { /* quota / disabled — non-fatal, attribute backup remains */ }
  }

  // ----- cart.attributes.line_item_order -----

  var attrTimer = null;
  function syncCartAttribute(order) {
    if (attrTimer) clearTimeout(attrTimer);
    attrTimer = setTimeout(function () {
      var body = { attributes: {} };
      body.attributes[ATTR_KEY] = order.join(',');
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).catch(function () { /* analytics-only mirror; ignore failures */ });
    }, 350);
  }

  // ----- DOM helpers -----

  function getContainers() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-items'));
  }

  function getRows(container) {
    return Array.prototype.slice.call(container.querySelectorAll(ROW_SEL));
  }

  // VISUAL order = sort rows by their current CSS `order` value (ascending).
  // Used for edge-button decisions and for moveRow's neighbor lookup —
  // never for DOM mutation.
  function getVisualRows(container) {
    return getRows(container).slice().sort(function (a, b) {
      return (Number(a.style.order) || 0) - (Number(b.style.order) || 0);
    });
  }

  // A Belimo line's "Tagging & Setup" panel renders as a separate companion
  // <tr class="cart-item__tagging-row"> immediately after its product row
  // (see cart-items.liquid). The tbody is a flex column, so a companion with
  // no explicit `order` would default to 0 and visually detach from its
  // product row whenever a saved order is applied. Copying the parent row's
  // order keeps them adjacent: equal order values fall back to DOM order,
  // and the companion always follows its parent in the DOM.
  function syncCompanionRow(row) {
    var next = row.nextElementSibling;
    if (next && next.classList && next.classList.contains('cart-item__tagging-row')) {
      next.style.order = row.style.order;
    }
  }

  function arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  function normalizeStoredOrder(rows, stored) {
    var legacyToCurrent = {};
    rows.forEach(function (row) {
      var legacyKey = row.getAttribute('data-legacy-line-key');
      var currentKey = row.getAttribute('data-line-key');
      if (legacyKey && currentKey && !legacyToCurrent[legacyKey]) {
        legacyToCurrent[legacyKey] = currentKey;
      }
    });
    return stored.map(function (key) {
      return legacyToCurrent[key] || key;
    });
  }

  // ----- Order reconciliation -----
  // Matched keys keep their saved relative order. Keys not in the saved order
  // (genuinely new items OR existing lines whose item.key changed via a tag
  // edit/split) stay at their CURRENT DOM position instead of being appended
  // to the end. This prevents a stale saved order from dumping a line to the
  // bottom on the next re-render (e.g. after a quantity change), and it
  // self-heals an already-stale saved order without needing to be cleared.
  function reconcileOrder(presentKeys, stored) {
    var storedRank = {};
    var r = 0;
    for (var j = 0; j < stored.length; j++) {
      if (!(stored[j] in storedRank)) storedRank[stored[j]] = r++;
    }

    var decorated = [];
    for (var i = 0; i < presentKeys.length; i++) {
      var key = presentKeys[i];
      var hasRank = key in storedRank;
      decorated.push({ key: key, dom: i, rank: hasRank ? storedRank[key] : null, sort: 0 });
    }

    // Unmatched keys inherit a fractional rank just after their preceding
    // matched neighbour (in DOM order), preserving their DOM position rather
    // than sinking to the bottom.
    var lastRank = -1;
    var frac = 0;
    for (var d = 0; d < decorated.length; d++) {
      var item = decorated[d];
      if (item.rank !== null) {
        lastRank = item.rank;
        frac = 0;
        item.sort = item.rank;
      } else {
        frac += 0.001;
        item.sort = lastRank + frac;
      }
    }

    decorated.sort(function (a, b) {
      return (a.sort - b.sort) || (a.dom - b.dom);
    });

    var ordered = [];
    var seen = {};
    for (var s = 0; s < decorated.length; s++) {
      var dk = decorated[s].key;
      if (!seen[dk]) { seen[dk] = true; ordered.push(dk); }
    }
    return ordered;
  }

  // ----- Apply CSS order to each row -----

  function applyOrderTo(container) {
    var rows = getRows(container);
    if (rows.length === 0) {
      updateEdgeButtons(container);
      return;
    }

    var present = rows.map(function (r) { return r.getAttribute('data-line-key'); });
    var rawStored = loadOrder();
    var stored = normalizeStoredOrder(rows, rawStored);
    var reconciled = reconcileOrder(present, stored);

    // Persist reconciled if it changed (stale prune / new tail items).
    if (!arraysEqual(rawStored, reconciled)) {
      saveOrder(reconciled);
      syncCartAttribute(reconciled);
    }

    var positionByKey = {};
    for (var i = 0; i < reconciled.length; i++) positionByKey[reconciled[i]] = i;

    rows.forEach(function (row) {
      var key = row.getAttribute('data-line-key');
      var pos = positionByKey[key];
      if (typeof pos !== 'number') pos = reconciled.length;
      row.style.order = String(pos);
      syncCompanionRow(row);
    });

    updateEdgeButtons(container);
  }

  // ----- Edge button visibility (based on VISUAL position) -----

  function updateEdgeButtons(container) {
    var visual = getVisualRows(container);
    var last = visual.length - 1;
    visual.forEach(function (row, idx) {
      setBtnState(row.querySelector(UP_SEL), idx === 0);
      setBtnState(row.querySelector(DOWN_SEL), idx === last);
    });
  }

  function setBtnState(btn, hidden) {
    if (!btn) return;
    btn.classList.toggle(HIDDEN_CLASS, hidden);
    if (hidden) {
      btn.setAttribute('aria-hidden', 'true');
      btn.setAttribute('tabindex', '-1');
      btn.disabled = true;
    } else {
      btn.removeAttribute('aria-hidden');
      btn.removeAttribute('tabindex');
      btn.disabled = false;
    }
  }

  // ----- Move (swap CSS order with neighbor) -----

  function moveRow(row, direction) {
    var container = row.closest('cart-items');
    if (!container) return null;
    var visual = getVisualRows(container);
    var idx = visual.indexOf(row);
    if (idx === -1) return null;
    var targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= visual.length) return null;

    var target = visual[targetIdx];
    var rowOrder = Number(row.style.order) || 0;
    var targetOrder = Number(target.style.order) || 0;
    // Rows with duplicate data-line-key (same variant AND same Tag — a rare
    // corner once tagged lines carry per-tag keys) share one order value, so
    // a plain swap would move nothing while still animating/announcing.
    // Renumber every row by its current visual position first (a stable
    // no-op visually — ties break by DOM order), which gives the two rows
    // distinct values so the swap below is a real move. Note: the persisted
    // key list still collapses duplicate keys, so the relative order of
    // duplicate-key rows reverts on the next full re-render; within this
    // render the button works and announces truthfully.
    if (rowOrder === targetOrder) {
      getVisualRows(container).forEach(function (r, i) {
        r.style.order = String(i);
        syncCompanionRow(r);
      });
      rowOrder = Number(row.style.order) || 0;
      targetOrder = Number(target.style.order) || 0;
    }
    row.style.order = String(targetOrder);
    target.style.order = String(rowOrder);
    syncCompanionRow(row);
    syncCompanionRow(target);

    // Persist by reading the new visual sequence.
    var newOrder = getVisualRows(container).map(function (r) {
      return r.getAttribute('data-line-key');
    });
    saveOrder(newOrder);
    syncCartAttribute(newOrder);

    updateEdgeButtons(container);

    // Mirror to any sibling cart-items containers (drawer + cart page rarely
    // coexist on the same render, but keep them in sync defensively).
    getContainers().forEach(function (other) {
      if (other !== container) applyOrderTo(other);
    });

    return { newIndex: targetIdx, total: visual.length };
  }

  // ----- Announcement -----

  function announce(row, info) {
    var container = row.closest('cart-items');
    if (!container) return;
    var region = container.querySelector(ANNOUNCE_SEL);
    if (!region) return;
    var titleEl = row.querySelector('.js-item-name');
    var title = titleEl ? titleEl.textContent.trim() : '';
    var template = (window.theme && window.theme.strings && window.theme.strings.cartReorderAnnounce) ||
      '[title], moved to position [position] of [total]';
    var msg = template
      .replace('[title]', title)
      .replace('[position]', String(info.newIndex + 1))
      .replace('[total]', String(info.total));
    region.textContent = '';
    setTimeout(function () { region.textContent = msg; }, 30);
  }

  // ----- Focus management -----

  function manageFocus(row, direction) {
    var primarySel = direction === 'up' ? UP_SEL : DOWN_SEL;
    var siblingSel = direction === 'up' ? DOWN_SEL : UP_SEL;
    var primary = row.querySelector(primarySel);
    if (primary && !primary.classList.contains(HIDDEN_CLASS)) {
      primary.focus();
      return;
    }
    var sibling = row.querySelector(siblingSel);
    if (sibling && !sibling.classList.contains(HIDDEN_CLASS)) sibling.focus();
  }

  // ----- Reduced-motion-aware flash -----

  function flashRow(row) {
    if (!window.matchMedia) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    row.classList.remove(FLASH_CLASS);
    void row.offsetWidth; // force reflow so animation re-triggers on rapid moves
    row.classList.add(FLASH_CLASS);
    setTimeout(function () { row.classList.remove(FLASH_CLASS); }, 700);
  }

  // ----- Click delegation -----

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(BTN_SEL);
    if (!btn) return;
    if (btn.disabled || btn.classList.contains(HIDDEN_CLASS)) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    var row = btn.closest(ROW_SEL);
    if (!row) return;
    var direction = btn.getAttribute('data-direction');
    var result = moveRow(row, direction);
    if (!result) return;
    flashRow(row);
    announce(row, result);
    manageFocus(row, direction);
  });

  // ----- Apply on render / re-render -----

  function applyToAll() {
    getContainers().forEach(applyOrderTo);
    clearPending();
  }

  function clearPending() {
    if (document.documentElement.hasAttribute(PENDING_ATTR)) {
      document.documentElement.removeAttribute(PENDING_ATTR);
    }
  }

  function rowsAffected(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      var added = m.addedNodes;
      for (var a = 0; a < added.length; a++) {
        var n = added[a];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(ROW_SEL)) return true;
        if (n.querySelector && n.querySelector(ROW_SEL)) return true;
      }
      var removed = m.removedNodes;
      for (var r = 0; r < removed.length; r++) {
        var rn = removed[r];
        if (rn.nodeType !== 1) continue;
        if (rn.matches && rn.matches(ROW_SEL)) return true;
        if (rn.querySelector && rn.querySelector(ROW_SEL)) return true;
      }
    }
    return false;
  }

  function observeContainer(container) {
    if (container.__bcsReorderObserved) return;
    container.__bcsReorderObserved = true;

    applyOrderTo(container);

    var mo = new MutationObserver(function (mutations) {
      if (!rowsAffected(mutations)) return;
      applyOrderTo(container);
      clearPending();
    });
    mo.observe(container, { childList: true, subtree: true });
  }

  function watchAll() {
    getContainers().forEach(observeContainer);

    // The cart drawer's <cart-items> can be replaced wholesale when the drawer
    // section innerHTML is refreshed. Watch the body for new cart-items.
    var bodyMo = new MutationObserver(function (mutations) {
      var found = false;
      for (var i = 0; i < mutations.length && !found; i++) {
        var added = mutations[i].addedNodes;
        for (var a = 0; a < added.length; a++) {
          var n = added[a];
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'CART-ITEMS' || (n.querySelector && n.querySelector('cart-items'))) {
            found = true;
            break;
          }
        }
      }
      if (found) getContainers().forEach(observeContainer);
    });
    bodyMo.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('on:line-item:change', function () {
    setTimeout(applyToAll, 0);
  });

  function init() {
    applyToAll();
    watchAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
